/**
 * 🔄 RSM数据适配器 - 统一版本
 *
 * 基于RSM协议的统一车辆数据管理
 * RSMMessage作为前后端统一的数据交互对象
 */

// === RSM协议数据结构定义 ===

/**
 * RSM消息格式（前后端统一标准）
 */
export interface RSMMessage {
  /** 消息类型，固定值"rsm" */
  msgType: 'rsm';
  /** RSU编号，八位字符串 */
  rsuId: string;
  /** 消息序列号 [0..127] */
  msgCnt?: number;
  /** UTC时间戳，精确到毫秒 */
  timestamp: number;
  /** 参考经度 [-180..180] */
  refLongitude?: number;
  /** 参考纬度 [-90..90] */
  refLatitude?: number;
  /** 参考高程 [-409.6..6143.9] */
  refElevation?: number;
  /** RSM包唯一ID */
  uuid: string;
  /** 交通参与者信息列表 */
  participants: RSMParticipant[];
}

/**
 * 车辆ID的推荐类型
 * - `plateNo`: 车牌号，最稳定
 * - `tempId`: 临时字符串ID
 * - `ptcId`: rsuId + ptcId 组合
 */
export type VehicleIdType = 'plateNo' | 'tempId' | 'ptcId';

/**
 * RSM参与者数据格式 - 系统标准数据结构
 */
export interface RSMParticipant {
  /** 参与者局部ID [0..65535] */
  ptcId: number;
  /** 参与者类型: 0-未知, 1-机动车, 2-非机动车, 3-行人, 4-RSU, 5-保留 */
  ptcType: 0 | 1 | 2 | 3 | 4 | 5;
  /** 数据来源: 0-未知, 1-自身, 2-V2X, 3-视觉, 4-毫米波雷达, 5-loop, 6-lidar, 7-integrated */
  sourceType: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
  /** 临时编号，8位字符串（可选） */
  id?: string;
  /** 车牌号，16位字符串（可选） */
  plateNo?: string;
  /** 目标检测UTC时间戳，毫秒 */
  ptcTimestamp: number;

  // 位置信息
  /** 经度 [-180..180]，小数点后7位 */
  longitude: number;
  /** 纬度 [-90..90]，小数点后7位 */
  latitude: number;
  /** 位置精度 [0..15] */
  posConfidence?: number;
  /** 高程 [-409.6..6143.9] */
  elevation?: number;
  /** 高程精度 [0..15] */
  eleConfidence?: number;

  // 运动信息
  /** 速度 [0..500] m/s */
  speed: number;
  /** 速度精度 [0..7] */
  spdConfidence?: number;
  /** 航向角 [0..360] 度，正北方向顺时针 */
  heading: number;
  /** 航向角精度 [0..7] */
  headConfidence?: number;
  /** 方向盘转角 [-126..127]，分辨率1.5度 */
  steeringAngle?: number;
  /** 方向盘转角精度 [0..3] */
  strConfidence?: number;

  // 加速度信息
  /** 纵向加速度 [-2000..+2001]，分辨率0.01 m/s² */
  lonAccel?: number;
  /** 横向加速度 [-2000..+2001]，分辨率0.01 m/s² */
  latAccel?: number;
  /** 垂直加速度 [-127..127]，分辨率0.02g */
  vertAccel?: number;
  /** 横摆角速度 [-32767..32767]，分辨率0.01°/s */
  yawRate?: number;

  // 车辆属性
  /** 挡位状态: 0-空挡, 1-停车挡, 2-前进挡, 3-倒挡, 7-无效 */
  transmission?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
  /** 目标物宽度 [0..1023] cm */
  width: number;
  /** 目标物长度 [0..4095] cm */
  length: number;
  /** 目标物高度 [0..127]，分辨率5cm */
  height?: number;
  /** 车辆类型 [0..255] */
  vehicleClass: number;
  /** 燃料类型 [0..10]: 0-未知, 1-汽油, 2-乙醇, 3-柴油, 4-电动, 5-混合动力等 */
  fuelType?: number;
}

/**
 * 车辆运行时信息（系统内部使用）
 */
export interface VehicleRuntimeInfo {
  /** 车辆唯一标识符，格式：rsuId_ptcId */
  vehicleId: string;
  /** RSU来源信息 */
  rsuId: string;
  /** 消息时间戳 */
  messageTimestamp: number;
  /** 系统状态 */
  systemStatus:
    | 'moving'
    | 'stopped'
    | 'parked'
    | 'offline'
    | 'warning'
    | 'error';
  /** 最后更新时间 */
  lastUpdated: number;
}

/**
 * 完整的车辆数据（RSM + 运行时信息）
 */
export interface VehicleData extends RSMParticipant {
  /** 运行时信息 */
  runtime: VehicleRuntimeInfo;
}

// === 数据源定义 ===

/**
 * 数组模式数据源
 */
export interface ArrayDataSource {
  type: 'array';
  scenario: {
    id: string;
    name: string;
    description?: string;
    duration: number;
    frameRate: number;
  };
  frames: Array<{
    frameId: number;
    timestamp: number;
    rsmMessages: RSMMessage[];
  }>;
  playbackConfig?: {
    autoPlay: boolean;
    loop: boolean;
    defaultSpeed: number;
  };
}

/**
 * WebSocket模式数据源
 */
export interface WebSocketDataSource {
  type: 'websocket';
  config: {
    url: string;
    protocols?: string[];
    reconnect?: {
      enabled: boolean;
      maxAttempts: number;
      interval: number;
    };
    buffer?: {
      maxSize: number;
      enablePause: boolean;
    };
    filters?: {
      rsuIds?: string[];
      ptcTypes?: number[];
    };
  };
}

// === RSM数据适配器工具类 ===

export class RSMDataAdapter {
  /**
   * 将RSM参与者转换为车辆数据
   */
  static participantToVehicleData(
    participant: RSMParticipant,
    rsuId: string,
    messageTimestamp: number
  ): VehicleData {
    const { vehicleId } = this.getStableVehicleId(participant, rsuId);

    // 处理真实RSM消息中的字段差异
    const normalizedParticipant = this.normalizeParticipant(participant);

    return {
      ...normalizedParticipant,
      runtime: {
        vehicleId,
        rsuId,
        messageTimestamp,
        systemStatus: this.inferSystemStatus(normalizedParticipant),
        lastUpdated: Date.now(),
      },
    };
  }

  /**
   * 获取稳定车辆ID
   * 优先使用 plateNo, 其次是 id, 最后是 rsuId_ptcId
   */
  static getStableVehicleId(
    participant: RSMParticipant,
    rsuId: string
  ): { vehicleId: string; type: VehicleIdType } {
    const rawParticipant = participant as any;
    const plateNo = participant.plateNo || rawParticipant.plateNO;

    if (plateNo && plateNo.length > 2) {
      return { vehicleId: plateNo, type: 'plateNo' };
    }
    if (participant.id && participant.id.length > 2) {
      return { vehicleId: participant.id, type: 'tempId' };
    }
    return {
      vehicleId: `${rsuId}_${participant.ptcId}`,
      type: 'ptcId',
    };
  }

  /**
   * 标准化参与者数据，处理真实RSM消息的格式差异
   */
  private static normalizeParticipant(
    participant: RSMParticipant
  ): RSMParticipant {
    const rawParticipant = participant as any;

    return {
      ...participant,
      // 处理车牌号字段名差异：plateNO -> plateNo
      plateNo: participant.plateNo || rawParticipant.plateNO || '',
      // 标准化尺寸数据
      width: this.normalizeDimension(participant.width, 180),
      length: this.normalizeDimension(participant.length, 450),
      height: this.normalizeDimension(participant.height, 150),
    };
  }

  /**
   * 标准化尺寸数据
   * 处理可能的单位差异和异常值
   */
  private static normalizeDimension(
    value: number | undefined,
    defaultValue: number
  ): number {
    if (!value || value <= 0) {
      return defaultValue;
    }

    // 如果数值过大（>50），已经是厘米单位
    if (value > 50) {
      // 但如果数值特别大（>10000），可能是毫米单位，转换为厘米
      if (value > 10000) {
        return Math.round(value / 10);
      }
      // 已经是厘米，直接返回
      return value;
    }

    // 如果数值较小（<=50），可能是米单位，转换为厘米
    return Math.round(value * 100);
  }

  /**
   * 将RSM消息转换为车辆数据列表
   */
  static convertRSMMessage(rsmMessage: RSMMessage): VehicleData[] {
    return rsmMessage.participants.map(participant =>
      this.participantToVehicleData(
        participant,
        rsmMessage.rsuId,
        rsmMessage.timestamp
      )
    );
  }

  /**
   * 批量转换RSM消息
   */
  static convertRSMBatch(rsmMessages: RSMMessage[]): VehicleData[] {
    return rsmMessages.flatMap(message => this.convertRSMMessage(message));
  }

  /**
   * 推断系统状态
   */
  private static inferSystemStatus(
    participant: RSMParticipant
  ): VehicleRuntimeInfo['systemStatus'] {
    if (participant.speed > 1) return 'moving';
    if (participant.speed > 0.1) return 'stopped';
    return 'parked';
  }

  /**
   * 映射RSM参与者类型到标准类型
   */
  static mapPtcTypeToString(ptcType: number): string {
    const typeMap: Record<number, string> = {
      0: 'unknown',
      1: 'vehicle',
      2: 'non_motor_vehicle',
      3: 'pedestrian',
      4: 'rsu',
      5: 'reserved',
    };
    return typeMap[ptcType] || 'unknown';
  }

  /**
   * 角度转换：度 -> 弧度
   */
  static degreesToRadians(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }

  /**
   * 角度转换：弧度 -> 度
   */
  static radiansToDegrees(radians: number): number {
    return (radians * 180) / Math.PI;
  }

  /**
   * 厘米转米
   */
  static cmToMeters(cm: number): number {
    return cm / 100;
  }

  /**
   * 获取车辆尺寸（转换为米）
   */
  static getVehicleDimensions(participant: RSMParticipant): {
    width: number;
    length: number;
    height?: number;
  } {
    return {
      width: this.cmToMeters(participant.width),
      length: this.cmToMeters(participant.length),
      height: participant.height ? participant.height * 0.05 : undefined,
    };
  }

  /**
   * 获取加速度（转换为m/s²）
   */
  static getAcceleration(participant: RSMParticipant): {
    longitudinal?: number;
    lateral?: number;
    vertical?: number;
  } {
    return {
      longitudinal: participant.lonAccel
        ? participant.lonAccel * 0.01
        : undefined,
      lateral: participant.latAccel ? participant.latAccel * 0.01 : undefined,
      vertical: participant.vertAccel
        ? participant.vertAccel * 0.02 * 9.80665
        : undefined,
    };
  }

  /**
   * 验证RSM消息
   */
  static validateRSMMessage(rsmMessage: RSMMessage): boolean {
    try {
      if (!rsmMessage.msgType || rsmMessage.msgType !== 'rsm') return false;
      if (!rsmMessage.rsuId || typeof rsmMessage.rsuId !== 'string')
        return false;
      if (!rsmMessage.timestamp || typeof rsmMessage.timestamp !== 'number')
        return false;
      if (!Array.isArray(rsmMessage.participants)) return false;

      return rsmMessage.participants.every(participant =>
        this.validateParticipant(participant)
      );
    } catch (error) {
      console.error('[RSMDataAdapter] 验证RSM消息失败:', error);
      return false;
    }
  }

  /**
   * 验证参与者数据
   */
  static validateParticipant(participant: RSMParticipant): boolean {
    try {
      // 必需字段检查
      const requiredFields = [
        'ptcId',
        'ptcType',
        'sourceType',
        'ptcTimestamp',
        'longitude',
        'latitude',
        'speed',
        'heading',
        'width',
        'length',
        'vehicleClass',
      ];

      for (const field of requiredFields) {
        if (typeof (participant as any)[field] !== 'number') return false;
      }

      // 范围检查
      if (participant.latitude < -90 || participant.latitude > 90) return false;
      if (participant.longitude < -180 || participant.longitude > 180)
        return false;
      if (participant.speed < 0 || participant.speed > 500) return false;
      if (participant.heading < 0 || participant.heading >= 360) return false;

      return true;
    } catch (error) {
      console.error('[RSMDataAdapter] 验证参与者数据失败:', error);
      return false;
    }
  }

  /**
   * 创建测试用RSM消息
   */
  static createTestRSMMessage(options?: {
    rsuId?: string;
    participantCount?: number;
    baseLatitude?: number;
    baseLongitude?: number;
  }): RSMMessage {
    const {
      rsuId = 'RSU_0001',
      participantCount = 5,
      baseLatitude = 25.101902,
      baseLongitude = 102.97146,
    } = options || {};

    const participants: RSMParticipant[] = [];

    for (let i = 0; i < participantCount; i++) {
      participants.push({
        ptcId: i + 1,
        ptcType: 1, // 机动车
        sourceType: 2, // V2X
        ptcTimestamp: Date.now(),
        longitude: baseLongitude + (Math.random() - 0.5) * 0.01,
        latitude: baseLatitude + (Math.random() - 0.5) * 0.01,
        speed: Math.random() * 20, // 0-20 m/s
        heading: Math.random() * 360,
        width: 180, // 1.8m
        length: 450, // 4.5m
        height: 30, // 1.5m
        vehicleClass: 1,
        plateNo: `云A${String(i + 1).padStart(5, '0')}`,
      });
    }

    return {
      msgType: 'rsm',
      rsuId,
      timestamp: Date.now(),
      uuid: `${rsuId}_${Date.now()}`,
      participants,
    };
  }
}

// === 兼容性导出 ===
export type ExtendedRSMParticipant = VehicleData;
